import AppButton from '@nxsys/components/AppButton';

import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useUpdateCompanyById } from '@nxsys/hooks/api/company';
import { AppDispatch } from '@nxsys/store';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { showMessage } from '@nxsys/utils/message';
import { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import RevenueEmployerSettingsForm, {
  RevenueEmployerSettingsFormValues,
} from './components/RevenueEmployerSettingsForm';
import RevenueNMWRates from './components/RevenueNMWRates';

type Props = {
  data?: ICompany;
  updateData: (params: UpdateCompanyByIdParams) => Promise<void>;
};

enum RevenueTabEnum {
  EMPLOYER_SETTINGS = 'EMPLOYER_SETTINGS',
  NMW_RATES = 'NMW_RATES',
}

const RevenueSetting: React.FC<Props> = ({ data }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { run: updateCompany, loading: isUpdatingCompany } =
    useUpdateCompanyById();
  const employerSettingsFormRef = useRef<{ submit: () => void } | null>(null);

  const tabs = useMemo(
    () => [
      {
        tab: messages['common.employerSettings'],
        key: RevenueTabEnum.EMPLOYER_SETTINGS,
      },
      {
        tab: messages['common.nmwRates'],
        key: RevenueTabEnum.NMW_RATES,
      },
    ],
    [messages],
  );

  const [activeTab, setActiveTab] = useState<RevenueTabEnum>(
    RevenueTabEnum.EMPLOYER_SETTINGS,
  );

  const handleTabChange = (key: RevenueTabEnum) => {
    setActiveTab(key);
  };

  const handleEmployerSettingsSubmit = async (
    values: RevenueEmployerSettingsFormValues,
  ) => {
    if (!data?.id) return;

    const params: UpdateCompanyByIdParams = {
      employerRegistrationNumber: values.employerRegistrationNumber || null,
      passwordP12: values.passwordP12 || null,
    };

    // Handle P12 file upload if a new file was selected
    const isUploadP12File = !!(values.p12FileLink as File)?.name;
    if (isUploadP12File) {
      try {
        const res = await dispatch(
          commonActions.uploadFileToS3({ file: values.p12FileLink as File }),
        ).unwrap();
        params.p12FileLink = res.url;
      } catch (error) {
        console.error(error);
        return;
      }
    } else if (typeof values.p12FileLink === 'string') {
      // Keep existing file URL if no new file was uploaded
      params.p12FileLink = values.p12FileLink;
    }

    const res = await updateCompany(data.id, params);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case RevenueTabEnum.EMPLOYER_SETTINGS:
        return (
          <RevenueEmployerSettingsForm
            companyData={data}
            formRef={employerSettingsFormRef}
            onSubmit={handleEmployerSettingsSubmit}
          />
        );
      case RevenueTabEnum.NMW_RATES:
        return <RevenueNMWRates />;
      default:
        return <></>;
    }
  };

  return (
    <AppCardTabListContainer
        tabList={tabs}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        className="!border-none"
      >
        <div className="p-[16px]">{renderTabContent()}</div>
        {data && activeTab === RevenueTabEnum.EMPLOYER_SETTINGS && (
          <div className="flex items-center justify-start gap-3 pt-4">
            <AppButton
              type="primary"
              name={messages['common.save']}
              leftIcon={
                <Icons.IconCheckCircle
                  color={defaultTheme.theme.palette.white}
                />
              }
              onClick={() => employerSettingsFormRef.current?.submit()}
              loading={isUpdatingCompany}
            />
          </div>
        )}
    </AppCardTabListContainer>
  );
};

export default RevenueSetting;
